import { useEffect, useRef, useState } from 'react'
import { AppState, Platform } from 'react-native'
import { useQueryClient } from '@tanstack/react-query'
import { getItem } from '../lib/storage'
import { refreshAccessToken } from '../lib/api'
import { STORAGE_KEYS, API_URL } from '../lib/constants'
import type {
  ServerEvent,
  ClientEvent,
  DirectMessageResponse,
} from '@warcarp/api-types'
import { applyGameSocketEvent } from '../lib/gameSocketEvents'
import {
  focusTransitionAction,
  isSocketStale,
  PING_INTERVAL_MS,
  LIVENESS_CHECK_MS,
  LIVENESS_STALE_MS,
} from '../lib/socketLiveness'
import { addBreadcrumb } from '../lib/reporting'

/** Epoch-ms when the most recent WebSocket connection opened for this session. */
export type ConnectedAtRef = React.MutableRefObject<number>

/**
 * Live connection status surfaced to the game screen so it can show a
 * non-blocking "Reconnecting…" banner instead of tearing down the screen
 * while the socket is re-establishing.
 *
 * - `connectedAt`: ref to the epoch-ms of the most recent successful open.
 * - `connected`: React state — true while a socket is open, false from the
 *   moment it closes until the next open. Drives the reconnecting banner.
 */
export interface GameSocketStatus {
  connectedAt: ConnectedAtRef
  connected: boolean
}

/**
 * Opens a WebSocket to /api/v1/games/:id/ws and keeps the React Query
 * game cache up-to-date from server-push events.
 *
 * Falls back gracefully: if the socket can't connect the query's refetchInterval
 * on useGame() handles polling.
 *
 * Focus tracking: sends FOCUS / BLUR messages to the server so it can
 * suppress notifications when the player is actively viewing the game.
 * - On React Native: driven by AppState change events.
 * - On web: driven by document.visibilitychange events.
 * AppState is skipped on web because its 'active' state does not track tab
 * visibility — only visibilitychange does.
 * On BOTH platforms, returning to the foreground with a dead socket triggers
 * an immediate reconnect (see focusTransitionAction in lib/socketLiveness).
 *
 * Default server-side state on connect: focused. Therefore the first message
 * we need to send is BLUR if the current environment is not active/visible at
 * connect time. We do NOT send FOCUS on connect — that would be wasteful and
 * trigger an unnecessary broadcast.
 */
/**
 * Returns a ref whose `.current` is the epoch-ms when the most recent
 * WebSocket connection opened. Starts at 0 (no connection yet) and updates
 * on each successful open, including reconnects.
 *
 * Consumers that want to skip "already-in-snapshot" events (e.g. bot attacks
 * that arrived while the user was off-screen) should treat events with
 * `ts < connectedAt.current` as historical and not animate them.
 */
export function useGameSocket(
  gameId: string | undefined,
  enabled: boolean,
  opts?: {
    /** Current user's id — needed to resolve the partner of an incoming DM. */
    currentUserId?: string
    /** Called for every DIRECT_MESSAGE frame (sent or received) so the screen
     *  can raise an in-app toast for messages addressed TO the user. */
    onDirectMessage?: (msg: DirectMessageResponse) => void
  },
): GameSocketStatus {
  const qc = useQueryClient()
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled

  // Kept in refs so the long-lived socket effect doesn't re-run (and reconnect)
  // when the callback identity or user id changes between renders.
  const currentUserIdRef = useRef(opts?.currentUserId)
  currentUserIdRef.current = opts?.currentUserId
  const onDirectMessageRef = useRef(opts?.onDirectMessage)
  onDirectMessageRef.current = opts?.onDirectMessage

  /** Epoch-ms of the most recent successful WS open for this session. */
  const connectedAtRef = useRef<number>(0)
  /** True while a socket is open; flips false the instant it closes. */
  const [connected, setConnected] = useState(false)

  useEffect(() => {
    if (!gameId || !enabled) return
    const activeGameId = gameId

    let destroyed = false
    let ws: WebSocket | null = null
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    let retryDelay = 2000
    // Consecutive 1008 (auth-rejected) closes handled by refresh-and-retry.
    // Capped so a genuine auth failure (logged out, revoked) can't loop.
    let authRetries = 0
    const MAX_AUTH_RETRIES = 3
    // Reconnects over this hook's lifetime — reported with the watchdog signal
    // so we can tell a one-off blip from a flapping connection.
    let reconnectCount = 0

    /** Reconnect unless a socket is already live or mid-connect. */
    function reconnectIfNeeded() {
      if (destroyed) return
      if (ws && (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN)) {
        return
      }
      if (retryTimer) {
        clearTimeout(retryTimer)
        retryTimer = null
      }
      // A foreground / visibility-driven reconnect is a fresh start — reset the
      // backoff so it doesn't inherit a grown delay from a prior flaky stretch.
      retryDelay = 2000
      void connect()
    }

    // ── Focus helpers ──────────────────────────────────────────────────────────

    function sendIfOpen(msg: ClientEvent) {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(msg))
      }
    }

    /**
     * Called once the socket opens (or re-opens after reconnect). The server
     * defaults new connections to focused=true. We only need to correct that
     * assumption if the current state is NOT focused.
     */
    function syncInitialFocusState() {
      const notFocused =
        Platform.OS === 'web'
          ? typeof document !== 'undefined' && document.visibilityState !== 'visible'
          : AppState.currentState !== 'active'
      if (notFocused) {
        sendIfOpen({ type: 'BLUR' })
      }
    }

    /**
     * React to a foreground/visibility change: re-assert focus on a live
     * socket, reconnect a dead one, or send BLUR on the way to the background.
     * See focusTransitionAction for the rationale of each branch.
     */
    function applyFocusTransition(isActive: boolean) {
      const action = focusTransitionAction(isActive, ws?.readyState === WebSocket.OPEN)
      if (action === 'focus') sendIfOpen({ type: 'FOCUS' })
      else if (action === 'reconnect') reconnectIfNeeded()
      else sendIfOpen({ type: 'BLUR' })
    }

    // ── Connection ─────────────────────────────────────────────────────────────

    async function connect() {
      if (destroyed) return
      // Don't open a second socket on top of a live/connecting one.
      if (ws && (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN)) {
        return
      }
      const token = await getItem(STORAGE_KEYS.ACCESS_TOKEN)
      if (!token || destroyed) return

      const wsBase = API_URL.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:')
      const socket = new WebSocket(`${wsBase}/api/v1/games/${gameId}/ws?token=${token}`)
      ws = socket

      // Epoch-ms of the last inbound frame on THIS connection (any type, incl.
      // the server's PONG). The liveness watchdog below compares against it to
      // catch half-open sockets that never fire `onclose`.
      let lastMessageAt = Date.now()
      // Diagnostic counters for THIS connection — reported if the watchdog trips
      // so we can tell "went silent after 3 bot events" (half-open) apart from
      // "never received anything".
      let messagesReceived = 0
      let lastMessageType = 'none'

      socket.onopen = () => {
        retryDelay = 2000 // reset backoff on success
        authRetries = 0 // a successful open clears the auth-retry budget
        setConnected(true)
        lastMessageAt = Date.now()
        addBreadcrumb({ category: 'ws', message: 'open', level: 'info', data: { reconnectCount } })
        // Record the wall-clock time this connection opened. Consumers
        // (e.g. useAttackEvents) compare event timestamps against this
        // value to decide whether to animate: events with ts < connectedAt
        // were already in the snapshot before the socket opened and should
        // not replay. Events with ts >= connectedAt arrived during this
        // session and should animate normally.
        connectedAtRef.current = Date.now()
        // Reset presence to empty so stale data from a previous game
        // doesn't briefly flash. The server immediately pushes the current
        // snapshot after the socket opens.
        qc.setQueryData<string[]>(['games', gameId, 'presence'], [])
        qc.setQueryData<string[]>(['games', gameId, 'presence-live'], [])
        // Correct the server's default-focused assumption if needed.
        syncInitialFocusState()
      }

      socket.onmessage = (e) => {
        // Any inbound frame proves the socket is alive — including the server's
        // PONG reply (which matches no branch below and is otherwise ignored).
        lastMessageAt = Date.now()
        messagesReceived++
        try {
          const event = JSON.parse(e.data as string) as ServerEvent
          lastMessageType = event.type ?? 'unknown'
          applyGameSocketEvent({
            gameId: activeGameId,
            event,
            qc,
            currentUserId: currentUserIdRef.current,
            onDirectMessage: onDirectMessageRef.current,
          })
        } catch {
          // Malformed frame — drop it, but leave a trace. Silent JSON.parse
          // failures here would otherwise hide a server/client contract drift.
          addBreadcrumb({ category: 'ws', message: 'message parse failed', level: 'warning' })
        }
      }

      socket.onclose = (e) => {
        if (destroyed) return
        setConnected(false)
        addBreadcrumb({
          category: 'ws',
          message: 'close',
          level: e.code === 1000 ? 'info' : 'warning',
          data: { code: e.code, messagesReceived, lastMessageType },
        })
        if (e.code === 1000) return // clean close (e.g. unmount)
        if (e.code === 1008) {
          // Auth rejected. The usual cause is an EXPIRED access token (the WS
          // carries the token in the connect URL and the server verifies it
          // only at connect). Refresh the token — exactly as the HTTP layer
          // does on a 401 — and reconnect. Capped so a genuine auth failure
          // (logged out / revoked refresh token) can't spin forever.
          // Without this the socket stayed dead, the player saw no live bot
          // updates, AND still got "your turn" pushes (the server saw no live
          // socket and so didn't suppress them).
          if (authRetries >= MAX_AUTH_RETRIES) return
          authRetries++
          refreshAccessToken()
            .then((tok) => {
              if (!destroyed && tok) void connect()
            })
            .catch((err: unknown) => {
              // A dead refresh token is an expected end-of-session path (for
              // example after logout on another device). Keep the diagnostic
              // context in breadcrumbs without creating a Sentry issue for a
              // normal reconnect failure.
              addBreadcrumb({
                category: 'ws',
                message: 'auth refresh failed during reconnect',
                level: 'warning',
                data: { platform: Platform.OS, authRetries, err: err instanceof Error ? err.message : String(err) },
              })
            })
          return
        }
        // Transient close (1006 etc.) — reconnect with capped backoff. Cap at
        // 10s (was 30s): on a flaky link the old cap left the player staring at
        // a dead/"connection problem" game for up to half a minute between
        // attempts. 10s recovers ~3× faster while still backing off.
        retryTimer = setTimeout(() => {
          retryDelay = Math.min(retryDelay * 2, 10_000)
          reconnectCount++
          void connect()
        }, retryDelay)
      }

      socket.onerror = () => socket.close()

      // Keepalive ping — the server replies with a PONG, refreshing lastMessageAt.
      const pingInterval = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'PING' }))
        }
      }, PING_INTERVAL_MS)

      // Liveness watchdog: if the socket is OPEN but has received nothing for
      // LIVENESS_STALE_MS despite our pings, it's half-open. Force-close it so
      // `onclose` fires and the normal backoff reconnect re-establishes a live
      // socket. NOTE the server pushes no game-state snapshot on connect (only
      // a PRESENCE_UPDATE) — catch-up on missed state comes from useGame's 15s
      // fallback poll while disconnected and from the foreground lifecycle
      // refresh after a background stay. This is the recovery path for the
      // Android "a few events then silence" report.
      const livenessInterval = setInterval(() => {
        if (
          socket.readyState === WebSocket.OPEN &&
          isSocketStale(lastMessageAt, Date.now(), LIVENESS_STALE_MS)
        ) {
          // DIAGNOSTIC: this is the signal that confirms (or refutes) the
          // half-open-socket theory for the "WS goes quiet mid-game" report.
          // If this fires, the socket was OPEN yet silent — `messagesReceived`
          // and `lastMessageType` tell us whether it died after delivering a
          // few bot events. If it NEVER fires while events still stop arriving,
          // the socket is alive (PONGs flowing) and the problem is server-side
          // (bot loop / event emitter), not the connection.
          // Downgraded from reportMessage (Sentry warning capture) to a
          // breadcrumb: the watchdog fires routinely on mobile network
          // transitions and generates high-volume WARCARP-APP-A/-9 issues.
          // It is self-healing recovery (we immediately close + reconnect),
          // not a code bug. The breadcrumb preserves timeline context for
          // any real errors that follow (e.g. a failed reconnect).
          const silentMs = Date.now() - lastMessageAt
          addBreadcrumb({
            category: 'ws',
            message: 'watchdog: half-open socket, forcing reconnect',
            level: 'warning',
            data: { silentMs, messagesReceived, lastMessageType, reconnectCount },
          })
          socket.close()
        }
      }, LIVENESS_CHECK_MS)

      socket.addEventListener('close', () => {
        clearInterval(pingInterval)
        clearInterval(livenessInterval)
      })
    }

    void connect()

    // ── AppState listener (React Native only, not web) ─────────────────────────

    let appStateCleanup: (() => void) | null = null

    if (Platform.OS !== 'web') {
      const subscription = AppState.addEventListener('change', (nextState) => {
        // States other than these three (e.g. 'unknown') carry no focus signal.
        if (nextState === 'active') {
          applyFocusTransition(true)
        } else if (nextState === 'background' || nextState === 'inactive') {
          applyFocusTransition(false)
        }
      })
      appStateCleanup = () => subscription.remove()
    }

    // ── visibilitychange listener (web only) ───────────────────────────────────

    let visibilityCleanup: (() => void) | null = null

    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const onVisibilityChange = () => {
        applyFocusTransition(document.visibilityState === 'visible')
      }
      document.addEventListener('visibilitychange', onVisibilityChange)
      visibilityCleanup = () => document.removeEventListener('visibilitychange', onVisibilityChange)
    }

    return () => {
      destroyed = true
      if (retryTimer) clearTimeout(retryTimer)
      ws?.close(1000, 'unmount')
      appStateCleanup?.()
      visibilityCleanup?.()
    }
  }, [gameId, enabled, qc])

  return { connectedAt: connectedAtRef, connected }
}
